import { ADMIN_API_URL } from './apiConfig';
import { fetchJsonWithAuth } from './authApi';

const ADMIN_PREFIX = `${ADMIN_API_URL}/api/v1/admin`;

async function request(path, options = {}) {
  const json = await fetchJsonWithAuth(ADMIN_PREFIX, path, options);
  return json?.data ?? json;
}

function get(path) {
  return request(path);
}

function post(path, body) {
  return request(path, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

function put(path, body) {
  return request(path, {
    method: 'PUT',
    body: JSON.stringify(body ?? {}),
  });
}

function del(path) {
  return request(path, { method: 'DELETE' });
}

export function getAllUsers() {
  return get('/users');
}

export function getUserId(user) {
  return user?.id ?? user?.userId ?? user?.user_id ?? user?.USER_ID;
}

export function getAllTeams() {
  return get('/teams');
}

export function updateTeam(team, payload) {
  const id = team?.id ?? team?.teamId ?? team?.team_id ?? team;
  return put(`/teams/${id}`, payload);
}

export function getAllRoles() {
  return get('/roles');
}

export function getRoleId(role) {
  return role?.id ?? role?.roleId ?? role?.role_id ?? role?.USER_TYPE_ID ?? role;
}

export function getModules() {
  return get('/modules');
}

export function getMenuModules() {
  return get('/menu-modules');
}

export function createMenuModule(payload) {
  return post('/menu-modules', payload);
}

export function updateMenuModule(id, payload) {
  return put(`/menu-modules/${id}`, payload);
}

export function deleteMenuModule(id) {
  return del(`/menu-modules/${id}`);
}

export function getMenuActions(moduleKey) {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return get(`/menu-actions${query}`);
}

export function createMenuAction(payload) {
  return post('/menu-actions', payload);
}

export function updateMenuAction(id, payload) {
  return put(`/menu-actions/${id}`, payload);
}

export function deleteMenuAction(id) {
  return del(`/menu-actions/${id}`);
}

export function getRolePermissions(role) {
  return get(`/roles/${getRoleId(role)}/permissions`);
}

export function updateRolePermissions(role, permissions) {
  return put(`/roles/${getRoleId(role)}/permissions`, { permissions });
}

export function getUserPermissions(user) {
  return get(`/users/${getUserId(user)}/permissions`);
}

export function updateUserPermissions(user, permissions) {
  return put(`/users/${getUserId(user)}/permissions`, { permissions });
}

export function getModuleFields(moduleKey) {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return get(`/module-fields${query}`);
}

export function getUserModuleFields(user, configType) {
  return get(`/users/${getUserId(user)}/module-fields?configType=${encodeURIComponent(configType ?? '')}`);
}

export function updateUserModuleFieldConfig(user, moduleKey, fields, configType) {
  return put(`/users/${getUserId(user)}/module-fields/${moduleKey}`, { fields, configType });
}

export function getTeamModuleFields(team, configType) {
  const id = team?.id ?? team?.teamId ?? team?.team_id ?? team;
  return get(`/teams/${id}/module-fields?configType=${encodeURIComponent(configType ?? '')}`);
}

export function updateTeamModuleFieldConfig(team, moduleKey, fields, configType) {
  const id = team?.id ?? team?.teamId ?? team?.team_id ?? team;
  return put(`/teams/${id}/module-fields/${moduleKey}`, { fields, configType });
}

export function getRoleModuleFields(role, configType) {
  return get(`/roles/${getRoleId(role)}/module-fields?configType=${encodeURIComponent(configType ?? '')}`);
}

export function updateRoleModuleFieldConfig(role, moduleKey, fields, configType) {
  return put(`/roles/${getRoleId(role)}/module-fields/${moduleKey}`, { fields, configType });
}

export function getAvailableCollections() {
  return get('/collections');
}

export function getCollectionFields(collectionName) {
  return get(`/collections/${encodeURIComponent(collectionName)}/fields`);
}

export function getArraySubfields(collectionName, fieldName) {
  return get(`/collections/${encodeURIComponent(collectionName)}/fields/${encodeURIComponent(fieldName)}/subfields`);
}

export function uploadListViewActionIcon(file) {
  const formData = new FormData();
  formData.append('file', file);
  return request('/uploads/list-view-action-icon', {
    method: 'POST',
    body: formData,
    headers: {},
  });
}

export function getRowActionConfigs(moduleKey) {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return get(`/row-action-configs${query}`);
}

export function createRowActionConfig(payload) {
  return post('/row-action-configs', payload);
}

export function updateRowActionConfig(id, payload) {
  return put(`/row-action-configs/${id}`, payload);
}

export function deleteRowActionConfig(id) {
  return del(`/row-action-configs/${id}`);
}

export function seedRowActionConfigs() {
  return post('/row-action-configs/seed');
}

export function getModuleActionRules(moduleKey) {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return get(`/module-action-rules${query}`);
}

export function createModuleActionRule(payload) {
  return post('/module-action-rules', payload);
}

export function updateModuleActionRule(id, payload) {
  return put(`/module-action-rules/${id}`, payload);
}

export function deleteModuleActionRule(id) {
  return del(`/module-action-rules/${id}`);
}

export function getAdminFormGroups(moduleKey = '') {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return get(`/form-groups${query}`);
}

export function createAdminFormGroup(payload) {
  return post('/form-groups', payload);
}

export function updateAdminFormGroup(id, payload) {
  return put(`/form-groups/${id}`, payload);
}

export function deleteAdminFormGroup(id) {
  return del(`/form-groups/${id}`);
}

export function seedAdminFormGroups() {
  return post('/form-groups/seed');
}

export function getCustomForms() {
  return get('/custom-forms');
}

export function createCustomForm(payload) {
  return post('/custom-forms', payload);
}

export function updateCustomForm(id, payload) {
  return put(`/custom-forms/${id}`, payload);
}

export function deleteCustomForm(id) {
  return del(`/custom-forms/${id}`);
}

export function getDetailConfig(moduleKey) {
  return get(`/detail-configs/${encodeURIComponent(moduleKey)}`);
}

export function saveDetailConfig(moduleKey, payload) {
  return put(`/detail-configs/${encodeURIComponent(moduleKey)}`, payload);
}

export function getModuleDetailPreview(moduleKey, recordId) {
  return get(`/detail-configs/${encodeURIComponent(moduleKey)}/preview/${encodeURIComponent(recordId)}`);
}

export function getDetailDefaults() {
  return get('/detail-defaults');
}

export function saveDetailDefaults(payload) {
  return put('/detail-defaults', payload);
}
